import React, { useState } from 'react';
import { useLang } from '../i18n/LangContext';
import { isNativeApp } from '../services/mcNative';

const API_BASE = process.env.API_BASE_URL || '';

function Step({ n, title, children }) {
  return (
    <div className="hg-step">
      <div className="hg-step-num">{n}</div>
      <div className="hg-step-body">
        <strong>{title}</strong>
        <div className="hg-step-desc">{children}</div>
      </div>
    </div>
  );
}

function Alert({ type, children }) {
  const icons = { info: 'fa-info-circle', warn: 'fa-exclamation-triangle', ok: 'fa-check-circle' };
  return (
    <div className={`hg-alert hg-alert--${type}`}>
      <i className={`fas ${icons[type] || icons.info}`}></i>
      <span>{children}</span>
    </div>
  );
}

function Problem({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`hg-problem ${open ? 'hg-problem--open' : ''}`}>
      <button className="hg-problem-q" onClick={() => setOpen(o => !o)}>
        <i className="fas fa-chevron-right hg-chevron"></i>
        <span>{title}</span>
      </button>
      {open && <div className="hg-problem-a">{children}</div>}
    </div>
  );
}

function TabStart() {
  const { t } = useLang();
  return (
    <div className="hg-tab-content">
      <p className="hg-lead">{t('help.start.lead')}</p>
      <h3><i className="fas fa-map-signs"></i> {t('help.start.h1')}</h3>
      <Step n="1" title={t('help.start.s1.title')}>
        {t('help.start.s1.desc_a')} <strong>{t('help.start.s1.desc_strong')}</strong> {t('help.start.s1.desc_b')}
      </Step>
      <Step n="2" title={t('help.start.s2.title')}>
        {t('help.start.s2.desc')}
      </Step>
      <Step n="3" title={t('help.start.s3.title')}>
        {t('help.start.s3.desc_a')} <strong>{t('help.start.s3.desc_tab')}</strong>{t('help.start.s3.desc_b')} <strong>{t('help.start.s3.desc_strong')}</strong>.
      </Step>
      <Alert type="info">{t('help.start.alert')}</Alert>
      <h3 style={{marginTop: '1.5rem'}}><i className="fas fa-lightbulb"></i> {t('help.start.h2')}</h3>
      <ul className="hg-list">
        <li><i className="fas fa-globe"></i> {t('help.start.l1')}</li>
        <li><i className="fas fa-blender"></i> {t('help.start.l2')}</li>
        <li><i className="fas fa-book"></i> {t('help.start.l3')}</li>
        <li><i className="fas fa-pen"></i> {t('help.start.l4')}</li>
        <li><i className="fas fa-photo-video"></i> {t('help.start.l5')}</li>
      </ul>
    </div>
  );
}

const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/ipjjoaopeimoooklbnjjkeaojjhenaig';

function TabExtension() {
  const { t } = useLang();
  return (
    <div className="hg-tab-content">
      <p className="hg-lead">{t('help.ext.lead')}</p>
      <Alert type="ok">{t('help.ext.alert')}</Alert>

      <div style={{textAlign: 'center', margin: '1.4rem 0'}}>
        <a
          href={CHROME_STORE_URL}
          target="_blank"
          rel="noreferrer"
          className="btn btn-primary"
          style={{display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem', padding: '0.7rem 1.4rem'}}
        >
          <i className="fab fa-chrome"></i> {t('ext.banner_cta')}
        </a>
      </div>

      <div className="hg-mobile-note">
        <div className="hg-mobile-note-title"><i className="fas fa-mobile-alt"></i> {t('help.ext.mobile.title')}</div>
        <p>{t('help.ext.mobile.desc_a')} <strong>{t('help.ext.mobile.desc_strong')}</strong> {t('help.ext.mobile.desc_b')}</p>
        <ul className="hg-list" style={{marginTop: 6}}>
          <li><i className="fas fa-clipboard"></i> {t('help.ext.mobile.l1_a')} <strong>{t('help.ext.mobile.l1_strong')}</strong>{t('help.ext.mobile.l1_b')}</li>
          <li><i className="fas fa-desktop"></i> {t('help.ext.mobile.l2')}</li>
        </ul>
      </div>

      <h3 style={{marginTop: '1.2rem'}}><i className="fas fa-list-ol"></i> {t('help.ext.h1')}</h3>
      <Step n="1" title={t('help.ext.s2.title')}>
        {t('help.ext.s2.desc_a')}<br />
        <a href={CHROME_STORE_URL} target="_blank" rel="noreferrer" className="hg-code" style={{textDecoration:'none'}}>Chrome Web Store — Matemeal Connector</a>
      </Step>
      <Step n="2" title={t('help.ext.s5.title')}>
        {t('help.ext.s5.desc')}
      </Step>
      <Step n="3" title={t('help.ext.s6.title')}>
        {t('help.ext.s6.desc_a')} <strong>F5</strong> {t('help.ext.s6.desc_b')} <em>{t('help.ext.s6.desc_c')}</em>
      </Step>
      <Alert type="ok">{t('help.ext.ok_alert')}</Alert>
      <h3 style={{marginTop: '1.2rem'}}><i className="fas fa-shield-alt"></i> {t('help.ext.security.title')}</h3>
      <ul className="hg-list">
        <li><i className="fas fa-lock"></i> {t('help.ext.security.l1')}</li>
        <li><i className="fas fa-ban"></i> {t('help.ext.security.l2')}</li>
        <li><i className="fas fa-eye-slash"></i> {t('help.ext.security.l3')}</li>
      </ul>
    </div>
  );
}

// Wariant zakładki „extension" dla aplikacji Android — zamiast instrukcji
// wtyczki Chrome opisuje wbudowane łączenie konta Monsieur Cuisine.
function TabAppConnect() {
  const { t } = useLang();
  return (
    <div className="hg-tab-content">
      <p className="hg-lead">{t('help.app.lead')}</p>
      <Alert type="ok">{t('help.app.alert')}</Alert>
      <h3 style={{marginTop: '1.2rem'}}><i className="fas fa-list-ol"></i> {t('help.app.h1')}</h3>
      <Step n="1" title={t('help.app.s1.title')}>
        {t('help.app.s1.desc')}
      </Step>
      <Step n="2" title={t('help.app.s2.title')}>
        {t('help.app.s2.desc')}
      </Step>
      <Step n="3" title={t('help.app.s3.title')}>
        {t('help.app.s3.desc')}
      </Step>
      <Alert type="info">{t('help.app.note')}</Alert>
      <h3 style={{marginTop: '1.2rem'}}><i className="fas fa-shield-alt"></i> {t('help.ext.security.title')}</h3>
      <ul className="hg-list">
        <li><i className="fas fa-lock"></i> {t('help.app.sec.l1')}</li>
        <li><i className="fas fa-eye-slash"></i> {t('help.app.sec.l2')}</li>
        <li><i className="fas fa-unlink"></i> {t('help.app.sec.l3')}</li>
      </ul>
    </div>
  );
}

function TabConvert() {
  const { t } = useLang();
  return (
    <div className="hg-tab-content">
      <p className="hg-lead">{t('help.conv.lead')}</p>
      <h3><i className="fas fa-paste"></i> {t('help.conv.h1')}</h3>
      <ul className="hg-list">
        <li><i className="fas fa-copy"></i> {t('help.conv.l1')}</li>
        <li><i className="fas fa-paste"></i> {t('help.conv.l2')}</li>
        <li><i className="fas fa-magic"></i> {t('help.conv.l3_a')} <strong>{t('help.conv.l3_strong')}</strong></li>
      </ul>
      <h3 style={{marginTop: '1.2rem'}}><i className="fas fa-cogs"></i> {t('help.conv.h2')}</h3>
      <div className="hg-cards">
        <div className="hg-card">
          <i className="fas fa-weight"></i>
          <strong>{t('help.conv.card1.title')}</strong>
          <span>{t('help.conv.card1.desc')}</span>
        </div>
        <div className="hg-card">
          <i className="fas fa-thermometer-half"></i>
          <strong>{t('help.conv.card2.title')}</strong>
          <span>{t('help.conv.card2.desc')}</span>
        </div>
        <div className="hg-card">
          <i className="fas fa-tachometer-alt"></i>
          <strong>{t('help.conv.card3.title')}</strong>
          <span>{t('help.conv.card3.desc')}</span>
        </div>
        <div className="hg-card">
          <i className="fas fa-clock"></i>
          <strong>{t('help.conv.card4.title')}</strong>
          <span>{t('help.conv.card4.desc')}</span>
        </div>
      </div>
      <h3 style={{marginTop: '1.2rem'}}><i className="fas fa-edit"></i> {t('help.conv.h3')}</h3>
      <ul className="hg-list">
        <li><i className="fas fa-pencil-alt"></i> {t('help.conv.edit.l1_a')} <strong>{t('help.conv.edit.l1_strong')}</strong> {t('help.conv.edit.l1_b')}</li>
        <li><i className="fas fa-check-square"></i> {t('help.conv.edit.l2_a')} <strong>{t('help.conv.edit.l2_strong')}</strong> {t('help.conv.edit.l2_b')}</li>
        <li><i className="fas fa-arrows-alt-v"></i> {t('help.conv.edit.l3')}</li>
      </ul>
      <h3 style={{marginTop: '1.2rem'}}><i className="fas fa-share-alt"></i> {t('help.conv.h4')}</h3>
      <ul className="hg-list">
        <li><i className="fas fa-clipboard"></i> {t('help.conv.share.l1_a')} <strong>{t('help.conv.share.l1_strong')}</strong> {t('help.conv.share.l1_b')}</li>
        <li><i className="fas fa-code"></i> {t('help.conv.share.l2_a')} <strong>{t('help.conv.share.l2_strong')}</strong> {t('help.conv.share.l2_b')}</li>
        <li><i className="fas fa-bookmark"></i> {t('help.conv.share.l3')}</li>
      </ul>
      <Alert type="info">{t('help.conv.alert')}</Alert>
    </div>
  );
}

function TabProblems() {
  const { t } = useLang();
  // W apce nie ma wtyczki Chrome — pytania o nią (p1, p2, p4) znikają,
  // pozostałe dostają wersje @app przez warstwę nadpisań w t()
  const native = isNativeApp();
  return (
    <div className="hg-tab-content">
      <p className="hg-lead">{t('help.prob.lead')}</p>
      <h3><i className="fas fa-plug"></i> {t('help.prob.h1')}</h3>

      {!native && <Problem title={t('help.prob.p1.q')}>
        <p>{t('help.prob.p1.desc')}</p>
        <ol className="hg-ol">
          <li>{t('help.prob.p1.l1')}</li>
          <li>{t('help.prob.p1.l2')}</li>
          <li>{t('help.prob.p1.l3_a')} <code>chrome://extensions</code>{t('help.prob.p1.l3_b')}</li>
        </ol>
      </Problem>}

      {!native && <Problem title={t('help.prob.p2.q')}>
        <ol className="hg-ol">
          <li>{t('help.prob.p2.l1_a')} <strong>monsieur-cuisine.com</strong> {t('help.prob.p2.l1_b')}</li>
          <li>{t('help.prob.p2.l2')}</li>
          <li>{t('help.prob.p2.l3')}</li>
        </ol>
      </Problem>}

      <Problem title={t('help.prob.p3.q')}>
        <p>{t('help.prob.p3.desc')}</p>
        <ol className="hg-ol">
          <li>{t('help.prob.p3.l1')}</li>
          <li>{t('help.prob.p3.l2')}</li>
        </ol>
      </Problem>

      {!native && <Problem title={t('help.prob.p4.q')}>
        <ol className="hg-ol">
          <li>{t('help.prob.p4.l1_a')} <strong>{t('help.prob.p4.l1_strong')}</strong> {t('help.prob.p4.l1_b')}</li>
          <li>{t('help.prob.p4.l2_a')} <strong>{t('help.prob.p4.l2_strong')}</strong> {t('help.prob.p4.l2_b')}</li>
          <li>{t('help.prob.p4.l3')}</li>
        </ol>
      </Problem>}

      <h3 style={{marginTop: '1.5rem'}}><i className="fas fa-robot"></i> {t('help.prob.h2')}</h3>

      <Problem title={t('help.prob.p5.q')}>
        <p>{t('help.prob.p5.desc')}</p>
        <ol className="hg-ol">
          <li>{t('help.prob.p5.l1')}</li>
          <li>{t('help.prob.p5.l2')}</li>
          <li>{t('help.prob.p5.l3')}</li>
        </ol>
      </Problem>

      <Problem title={t('help.prob.p6.q')}>
        <ol className="hg-ol">
          <li>{t('help.prob.p6.l1')}</li>
          <li>{t('help.prob.p6.l2')}</li>
          <li>{t('help.prob.p6.l3_a')} <strong>{t('help.prob.p6.l3_strong')}</strong> {t('help.prob.p6.l3_b')}</li>
          <li>{t('help.prob.p6.l4_a')} <strong>{t('help.prob.p6.l4_strong')}</strong> {t('help.prob.p6.l4_b')}</li>
        </ol>
      </Problem>

      <Problem title={t('help.prob.p7.q')}>
        <p>{t('help.prob.p7.desc')}</p>
        <ol className="hg-ol">
          <li>{t('help.prob.p7.l1')}</li>
          <li>{t('help.prob.p7.l2')}</li>
        </ol>
      </Problem>

      <h3 style={{marginTop: '1.5rem'}}><i className="fas fa-question-circle"></i> {t('help.prob.h3')}</h3>

      <Problem title={t('help.prob.p8.q')}>
        <ol className="hg-ol">
          <li>{t('help.prob.p8.l1')}</li>
          <li>{t('help.prob.p8.l2')}</li>
          <li>{t('help.prob.p8.l3')}</li>
        </ol>
      </Problem>

      <Problem title={t('help.prob.p9.q')}>
        <p>{t('help.prob.p9.desc')}</p>
      </Problem>

      <ContactForm />
    </div>
  );
}

function ContactForm() {
  const { t } = useLang();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [replyEmail, setReplyEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | success | error | rate_limit

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !message.trim()) return;
    setStatus('sending');
    try {
      const res = await fetch(`${API_BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject_type: subject, message: message.trim(), reply_email: replyEmail.trim() }),
      });
      if (res.status === 429) { setStatus('rate_limit'); return; }
      if (!res.ok) { setStatus('error'); return; }
      setStatus('success');
      setMessage('');
      setReplyEmail('');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="hg-contact-form">
        <Alert type="ok">{t('contact.form.success')}</Alert>
      </div>
    );
  }

  return (
    <div className="hg-contact-form">
      <h3><i className="fas fa-envelope"></i> {t('contact.form.title')}</h3>
      <form onSubmit={handleSubmit} className="hg-form">
        <div className="hg-form-row">
          <label className="hg-label">{t('contact.form.subject')}</label>
          <select className="hg-select" value={subject} onChange={e => setSubject(e.target.value)} required>
            <option value="">—</option>
            <option value="bug">{t('contact.form.subject.bug')}</option>
            <option value="help">{t('contact.form.subject.help')}</option>
            <option value="other">{t('contact.form.subject.other')}</option>
          </select>
        </div>
        <div className="hg-form-row">
          <label className="hg-label">{t('contact.form.message')}</label>
          <textarea
            className="hg-textarea"
            rows={5}
            value={message}
            onChange={e => setMessage(e.target.value)}
            required
            maxLength={5000}
          />
        </div>
        <div className="hg-form-row">
          <label className="hg-label">{t('contact.form.reply_email')}</label>
          <input
            className="hg-input"
            type="email"
            value={replyEmail}
            onChange={e => setReplyEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>
        {(status === 'error' || status === 'rate_limit') && (
          <Alert type="warn">{t(status === 'rate_limit' ? 'contact.form.rate_limit' : 'contact.form.error')}</Alert>
        )}
        <button className="btn btn-primary hg-form-submit" type="submit" disabled={status === 'sending'}>
          <i className="fas fa-paper-plane"></i>{' '}
          {status === 'sending' ? t('contact.form.sending') : t('contact.form.send')}
        </button>
      </form>
    </div>
  );
}

const TAB_COMPONENTS = { start: TabStart, extension: TabExtension, convert: TabConvert, problems: TabProblems };

function HelpGuide({ onClose }) {
  const { t } = useLang();
  const [activeTab, setActiveTab] = useState('start');
  const native = isNativeApp();
  const TabComponent = (native && activeTab === 'extension') ? TabAppConnect : TAB_COMPONENTS[activeTab];

  const TABS = [
    { id: 'start',     icon: 'fas fa-rocket',            label: t('help.tab.start') },
    { id: 'extension', icon: native ? 'fas fa-link' : 'fas fa-puzzle-piece', label: native ? t('help.tab.app') : t('help.tab.extension') },
    { id: 'convert',   icon: 'fas fa-blender',            label: t('help.tab.convert') },
    { id: 'problems',  icon: 'fas fa-exclamation-circle', label: t('help.tab.problems') },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="hg-modal" onClick={e => e.stopPropagation()}>
        <div className="hg-header">
          <div className="hg-header-title">
            <i className="fas fa-book-open"></i>
            <h2>{t('help.title')}</h2>
          </div>
          <button className="hg-close" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>
        <div className="hg-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`hg-tab ${activeTab === tab.id ? 'hg-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={tab.icon}></i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
        <div className="hg-body">
          <TabComponent />
        </div>
      </div>
    </div>
  );
}

export default HelpGuide;
